/**
 * PreOwnershipReviewDialog — iter159j
 *
 * Zeigt alle Buchungen des Mietverhältnisses, die als Vor-Eigentümer-
 * Buchung markiert wurden (auto-erkannt ODER manuell zugewiesen). Der
 * Nutzer wählt gezielt aus, welche Zuweisungen aufgehoben werden sollen —
 * kein „alles blind rückgängig" mehr.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowCounterClockwise } from "@phosphor-icons/react";

const CATEGORY_LABELS = {
  rent_cold: "Kaltmiete", parking: "Stellplatz", utilities: "Nebenkosten",
  heating: "Heizung", vat: "USt", other: "Sonstige", deposit: "Kaution",
  fee: "Mahngebühr", dunning_fee: "Mahnkosten",
};
const eur = (v) => (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmt = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

export default function PreOwnershipReviewDialog({ open, onOpenChange, tenancyId, onReverted }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!tenancyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tenancies/${tenancyId}/postings`, {
        params: { include_pre_ownership: true },
      });
      const all = data?.postings || [];
      const pre = all.filter((p) => p.pre_ownership);
      setItems(pre);
      setSelected({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, tenancyId]);

  const toggleAll = (val) => {
    const next = {};
    if (val) items.forEach((p) => { next[p.id] = true; });
    setSelected(next);
  };
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allSelected = items.length > 0 && selectedIds.length === items.length;

  const doRevert = async () => {
    if (selectedIds.length === 0) {
      toast.error("Bitte mindestens eine Buchung auswählen");
      return;
    }
    setBusy(true);
    try {
      let done = 0;
      for (const pid of selectedIds) {
        await api.patch(`/tenancies/${tenancyId}/postings/${pid}/toggle-pre-ownership`,
                        null, { params: { pre_ownership: false } });
        done++;
      }
      toast.success(`${done} Zuweisung(en) zurückgesetzt`);
      if (typeof onReverted === "function") onReverted();
      // Neu laden, damit die Liste die verbleibenden zeigt
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="pre-ownership-review-dialog">
        <DialogHeader>
          <DialogTitle>Vor-Eigentümer-Zuweisungen prüfen</DialogTitle>
          <DialogDescription>
            Alle Buchungen dieses Mietverhältnisses, die als „Vor-Eigentümer"
            markiert sind (auto-erkannt oder manuell zugewiesen). Wählen Sie
            gezielt jene aus, deren Zuweisung aufgehoben werden soll — diese
            zählen dann wieder ins Mietkonto.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-slate-500">Lade …</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500">
            Keine als Vor-Eigentümer markierten Buchungen gefunden.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-slate-600 px-1 pb-1 border-b">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(v) => toggleAll(!!v)}
                  data-testid="pre-own-select-all"
                />
                <span className="font-medium">
                  Alle auswählen ({items.length})
                </span>
              </label>
              <span className="tabular-nums text-slate-500">
                {selectedIds.length} ausgewählt
              </span>
            </div>
            <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100 border-t border-b border-slate-200">
              {items.map((p) => (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 px-2 py-2 text-sm hover:bg-slate-50 cursor-pointer ${
                    selected[p.id] ? "bg-amber-50/60" : ""
                  }`}
                  data-testid={`pre-own-row-${p.id}`}
                >
                  <Checkbox
                    checked={!!selected[p.id]}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, [p.id]: !!v }))}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="tabular-nums font-medium text-slate-900">
                        {fmt(p.posting_date)}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                        {p.type === "soll" ? "Soll" : "Ist"}
                      </span>
                      <span className="text-xs text-slate-600">
                        {CATEGORY_LABELS[p.category] || p.category}
                      </span>
                      {p.pre_ownership_overridden && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                          manuell
                        </span>
                      )}
                    </div>
                    {p.reference && (
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {p.reference}
                      </div>
                    )}
                  </div>
                  <div className="tabular-nums font-semibold text-slate-900 whitespace-nowrap">
                    {eur(p.amount)}
                  </div>
                </label>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
          <Button
            onClick={doRevert}
            disabled={busy || selectedIds.length === 0}
            className="gap-1.5"
            data-testid="pre-own-revert-selected-btn"
          >
            <ArrowCounterClockwise size={14} />
            {busy ? "Setze zurück…" : `${selectedIds.length || ""} Auswahl zurücksetzen`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
